import React, { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import SunCalc from 'suncalc'

const ControlsContainer = styled.div`
  padding: 12px 0 8px 0;
  margin-left: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const SliderLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SliderValue = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;
`

const Slider = styled.input`
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    transition: all 0.15s ease;
    border: 2px solid ${({ theme }) => theme.colors.background.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    &:hover {
      transform: scale(1.2);
    }
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    border: 2px solid ${({ theme }) => theme.colors.background.primary};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: all 0.15s ease;

    &:hover {
      transform: scale(1.2);
    }
  }
`

const PresetButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`

const PresetButton = styled.button<{ $isActive?: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.colors.primary : theme.colors.border.primary};
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primaryLight : theme.colors.background.primary};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.primary : theme.colors.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`

const SectionDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border.light};
  margin: 4px 0;
`

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TimelineBar = styled.div`
  position: relative;
  width: 100%;
  height: 24px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: ${({ theme }) => theme.colors.background.tertiary};
`

const TimelineSegment = styled.div<{
  $left: number
  $width: number
  $color: string
}>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: ${({ $left }) => $left}%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
`

const TimelineMarker = styled.div<{ $left: number }>`
  position: absolute;
  top: -2px;
  bottom: -2px;
  left: ${({ $left }) => $left}%;
  width: 3px;
  background: ${({ theme }) => theme.colors.text.primary};
  border-radius: 2px;
  transform: translateX(-50%);
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
  z-index: 1;
`

const TimelineLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export interface SunlightLayerControlsProps {
  selectedLayerId: string
  currentDate?: Date
  onDateChange: (date: Date) => void
  /** Key to force re-render when date changes */
  dateKey?: string
  /** Map center coordinates [lng, lat] for computing sun times */
  coordinates?: [number, number]
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

// Convert day of year (1-365) to Date
const dayOfYearToDate = (
  dayOfYear: number,
  year: number = new Date().getFullYear()
): Date => {
  const date = new Date(year, 0, 1)
  date.setDate(dayOfYear)
  return date
}

// Convert Date to day of year (1-365)
const dateToDayOfYear = (date: Date): number => {
  const start = new Date(date.getFullYear(), 0, 0)
  const diff = date.getTime() - start.getTime()
  const oneDay = 1000 * 60 * 60 * 24
  return Math.floor(diff / oneDay)
}

// Get month name and day from day of year
const getDayOfYearLabel = (
  dayOfYear: number,
  year: number = new Date().getFullYear()
): string => {
  const date = dayOfYearToDate(dayOfYear, year)
  const month = MONTH_NAMES[date.getMonth()]
  const day = date.getDate()
  return `${month} ${day}`
}

// Get time label in HH:MM format
const getTimeLabel = (hours: number): string => {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

// Default coordinates for Geneva, Switzerland
const DEFAULT_COORDINATES: [number, number] = [6.1424, 46.2022]

// Convert a Date to fractional hours (0-24)
const dateToHours = (date: Date): number =>
  date.getHours() + date.getMinutes() / 60

export const SunlightLayerControls: React.FC<SunlightLayerControlsProps> = ({
  selectedLayerId,
  currentDate = new Date(),
  onDateChange,
  dateKey,
  coordinates = DEFAULT_COORDINATES,
}) => {
  const { t } = useTranslation('ui')
  const isShadowMode = selectedLayerId === 'sunlight-shadow-simulation'
  const isSunlightHoursMode = selectedLayerId === 'sunlight-hours'
  const year = currentDate.getFullYear()

  // Compute dynamic sun times for the selected date and location
  const sunTimes = useMemo(() => {
    const [lng, lat] = coordinates
    const times = SunCalc.getTimes(currentDate, lat, lng)
    const sunrise = dateToHours(times.sunrise)
    const sunset = dateToHours(times.sunset)
    const goldenHourAm = dateToHours(times.goldenHourEnd) // end of morning golden hour
    const goldenHourPm = dateToHours(times.goldenHour) // start of evening golden hour
    return { sunrise, sunset, goldenHourAm, goldenHourPm }
  }, [currentDate.getTime(), coordinates[0], coordinates[1]])

  // State for sliders
  const [dayOfYear, setDayOfYear] = useState(() => dateToDayOfYear(currentDate))
  const [timeOfDay, setTimeOfDay] = useState(() => {
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()
    return hours + minutes / 60
  })

  // Update local state when currentDate or dateKey changes
  useEffect(() => {
    const newDayOfYear = dateToDayOfYear(currentDate)
    const hours = currentDate.getHours()
    const minutes = currentDate.getMinutes()
    const newTimeOfDay = hours + minutes / 60

    setDayOfYear(newDayOfYear)
    setTimeOfDay(newTimeOfDay)
  }, [currentDate, dateKey])

  // Handle day of year change
  const handleDayChange = useCallback(
    (value: number) => {
      setDayOfYear(value)
      const newDate = dayOfYearToDate(value, year)
      newDate.setHours(Math.floor(timeOfDay))
      newDate.setMinutes(Math.round((timeOfDay % 1) * 60))
      // Call immediately for real-time updates - no debouncing needed
      onDateChange(newDate)
    },
    [year, timeOfDay, onDateChange]
  )

  // Handle time of day change (shadow mode only)
  const handleTimeChange = useCallback(
    (value: number) => {
      setTimeOfDay(value)
      const newDate = dayOfYearToDate(dayOfYear, year)
      newDate.setHours(Math.floor(value))
      newDate.setMinutes(Math.round((value % 1) * 60))
      // Call immediately for real-time updates - no debouncing needed
      onDateChange(newDate)
    },
    [dayOfYear, year, onDateChange]
  )

  // Preset buttons - recreate dates each time to get current "now"
  const handlePresetClick = (
    presetType: 'now' | 'summer' | 'winter' | 'spring'
  ) => {
    const now = new Date()
    const currentYear = now.getFullYear()

    let date: Date
    switch (presetType) {
      case 'now':
        // For sunlight hours mode, use today at noon (time doesn't matter)
        // For shadow mode, use current time
        date = isSunlightHoursMode
          ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0)
          : now
        break
      case 'summer':
        date = new Date(currentYear, 5, 21, 12, 0) // June 21, noon
        break
      case 'winter':
        date = new Date(currentYear, 11, 21, 12, 0) // December 21, noon
        break
      case 'spring':
        date = new Date(currentYear, 2, 20, 12, 0) // March 20, noon
        break
    }

    onDateChange(date)
  }

  return (
    <ControlsContainer>
      <SectionDivider />
      {/* Day of Year Slider */}
      <SliderGroup>
        <SliderLabel>
          <span>{t('analysisMap.sunlightControls.date')}</span>
          <SliderValue>{getDayOfYearLabel(dayOfYear, year)}</SliderValue>
        </SliderLabel>
        <Slider
          type="range"
          min="1"
          max="365"
          value={dayOfYear}
          onChange={(e) => handleDayChange(parseInt(e.target.value))}
        />
      </SliderGroup>
      {/* Time of Day Slider (Shadow Mode Only - not needed for Sunlight Hours) */}
      {isShadowMode && (
        <SliderGroup>
          <SliderLabel>
            <span>{t('analysisMap.sunlightControls.time')}</span>
            <SliderValue>{getTimeLabel(timeOfDay)}</SliderValue>
          </SliderLabel>
          <Slider
            type="range"
            min="0"
            max="24"
            step="0.25"
            value={timeOfDay}
            onChange={(e) => handleTimeChange(parseFloat(e.target.value))}
          />
        </SliderGroup>
      )}
      {/* Sunlight Hours Timeline Bar */}
      <TimelineContainer>
        <SliderLabel>
          <span>{t('analysisMap.sunlightControls.timeline')}</span>
        </SliderLabel>
        <TimelineBar
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect()
            const x = e.clientX - rect.left
            const pct = x / rect.width
            const hour = pct * 24
            if (isShadowMode) {
              handleTimeChange(Math.max(0, Math.min(24, hour)))
            }
          }}
        >
          {/* Night (before sunrise) */}
          <TimelineSegment
            $left={0}
            $width={(sunTimes.sunrise / 24) * 100}
            $color="#1a1a2e"
          />
          {/* Dawn / Golden hour (sunrise to end of morning golden hour) */}
          <TimelineSegment
            $left={(sunTimes.sunrise / 24) * 100}
            $width={((sunTimes.goldenHourAm - sunTimes.sunrise) / 24) * 100}
            $color="#ff9500"
          />
          {/* Daylight (end of morning golden hour to start of evening golden hour) */}
          <TimelineSegment
            $left={(sunTimes.goldenHourAm / 24) * 100}
            $width={
              ((sunTimes.goldenHourPm - sunTimes.goldenHourAm) / 24) * 100
            }
            $color="#FFD700"
          />
          {/* Dusk / Golden hour (start of evening golden hour to sunset) */}
          <TimelineSegment
            $left={(sunTimes.goldenHourPm / 24) * 100}
            $width={((sunTimes.sunset - sunTimes.goldenHourPm) / 24) * 100}
            $color="#ff9500"
          />
          {/* Night (after sunset) */}
          <TimelineSegment
            $left={(sunTimes.sunset / 24) * 100}
            $width={((24 - sunTimes.sunset) / 24) * 100}
            $color="#1a1a2e"
          />
          {/* Current time marker */}
          {isShadowMode && <TimelineMarker $left={(timeOfDay / 24) * 100} />}
        </TimelineBar>
        <TimelineLabels>
          <span>00:00</span>
          <span>06:00</span>
          <span>12:00</span>
          <span>18:00</span>
          <span>24:00</span>
        </TimelineLabels>
      </TimelineContainer>
      {/* Info message for Sunlight Hours mode */}
      {isSunlightHoursMode && (
        <SliderLabel
          style={{ fontSize: '11px', fontStyle: 'italic', opacity: 0.7 }}
        >
          {t('analysisMap.sunlightControls.sunExposureInfo')}
        </SliderLabel>
      )}
      {/* Preset Buttons */}
      <PresetButtons>
        <PresetButton onClick={() => handlePresetClick('now')}>
          {isShadowMode
            ? t('analysisMap.sunlightControls.now')
            : t('analysisMap.sunlightControls.today')}
        </PresetButton>
        <PresetButton onClick={() => handlePresetClick('summer')}>
          {t('analysisMap.sunlightControls.summer')}
        </PresetButton>
        <PresetButton onClick={() => handlePresetClick('winter')}>
          {t('analysisMap.sunlightControls.winter')}
        </PresetButton>
        <PresetButton onClick={() => handlePresetClick('spring')}>
          {t('analysisMap.sunlightControls.spring')}
        </PresetButton>
      </PresetButtons>
    </ControlsContainer>
  )
}
